import { useCallback, useEffect, useState } from 'react';
import { Input } from 'reactstrap';
import { AsYouType, CountryCode } from 'libphonenumber-js';

interface PhoneInputProps {
  className?: string;
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  disabled?: boolean;
  bsSize?: 'sm' | 'lg';
  defaultCountry?: CountryCode;
  autoComplete?: string;
}

const PhoneInput = ({
  className,
  value = '',
  onChange,
  onBlur,
  placeholder,
  disabled = false,
  bsSize = 'lg',
  defaultCountry = 'GB',
  autoComplete = 'tel',
}: PhoneInputProps): JSX.Element => {
  const [displayValue, setDisplayValue] = useState('');
  const [asYouType] = useState(() => new AsYouType(defaultCountry));

  // Initialize display value when component mounts or value changes externally
  useEffect(() => {
    if (value) {
      asYouType.reset();
      const formatted = asYouType.input(value);
      setDisplayValue(formatted);
    } else {
      setDisplayValue('');
      asYouType.reset();
    }
  }, [value, asYouType]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;

      // Reset AsYouType formatter and input the new value
      asYouType.reset();
      const formatted = asYouType.input(inputValue);

      setDisplayValue(formatted);

      // Get the phone number object to extract the formatted number
      const phoneNumber = asYouType.getNumber();
      const formattedNumber = phoneNumber ? phoneNumber.format('E.164') : '';

      // Call onChange with the E.164 formatted number (includes + prefix)
      if (onChange) {
        onChange(formattedNumber);
      }
    },
    [asYouType, onChange],
  );

  const handleBlur = useCallback(() => {
    if (onBlur) {
      onBlur();
    }
  }, [onBlur]);

  return (
    <Input
      className={className}
      type="tel"
      value={displayValue}
      onChange={handleInputChange}
      onBlur={handleBlur}
      placeholder={placeholder}
      disabled={disabled}
      bsSize={bsSize}
      autoComplete={autoComplete}
    />
  );
};

export default PhoneInput;
